import React, { useEffect, useState, useRef, createElement } from 'react';
import { TypeAnimation } from 'react-type-animation';
import { ArrowDownIcon, FileTextIcon, MailIcon, LinkedinIcon, ExternalLinkIcon, GlobeIcon } from 'lucide-react';
import { Link } from 'react-scroll';
const Hero = ({
  language,
  toggleLanguage
}) => {
  const particlesRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [isMobile, setIsMobile] = useState(false);
  // Check if mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Create particle effect
  useEffect(() => {
    const createParticle = () => {
      const particles = particlesRef.current;
      if (!particles) return;
      // Create fewer particles on mobile
      if (isMobile && Math.random() > 0.5) return;
      const particle = document.createElement('div');
      particle.classList.add('particle');
      // Random position, size and animation duration
      const size = Math.random() * (isMobile ? 6 : 10) + (isMobile ? 3 : 5);
      const posX = Math.random() * window.innerWidth;
      const posY = window.innerHeight;
      const duration = Math.random() * 10 + 10;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.left = `${posX}px`;
      particle.style.bottom = '0';
      particle.style.animationDuration = `${duration}s`;
      particles.appendChild(particle);
      // Remove particle after animation completes
      setTimeout(() => {
        if (particles.contains(particle)) {
          particles.removeChild(particle);
        }
      }, duration * 1000);
    };
    // Create particles at intervals - less frequent on mobile
    const particleInterval = setInterval(createParticle, isMobile ? 1200 : 800);
    return () => clearInterval(particleInterval);
  }, [isMobile]);
  // Parallax effect - reduced on mobile
  useEffect(() => {
    const handleMouseMove = e => {
      if (isMobile) return; // Disable parallax on mobile for better performance
      setMousePosition({
        x: e.clientX / window.innerWidth - 0.5,
        y: e.clientY / window.innerHeight - 0.5
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);
  // Add ripple effect to buttons
  const createRipple = event => {
    const button = event.currentTarget;
    const circle = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${event.clientX - button.getBoundingClientRect().left - radius}px`;
    circle.style.top = `${event.clientY - button.getBoundingClientRect().top - radius}px`;
    circle.classList.add('ripple');
    const ripple = button.getElementsByClassName('ripple')[0];
    if (ripple) {
      ripple.remove();
    }
    button.appendChild(circle);
  };
  // Scroll down animation
  const scrollToNext = () => {
    document.getElementById('logos').scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // Translations
  const translations = {
    en: {
      bio: 'Northeastern University Finance student with experience at Orient Commercial Bank, HFIC, Westlake Securities, Springer Capital, Cavalina Property Group, and VinaCapital. Founder of AidWorks Foundation.',
      peopleImpacted: '93,500+ People Impacted',
      volunteers: '100+ Volunteers Led',
      scrollDown: 'Scroll Down',
      resume: 'Resume',
      contact: 'Contact',
      connectOnLinkedin: 'Connect on LinkedIn',
      typewriterTexts: ['Finance Student', 2000, 'Entrepreneur', 2000, 'Nonprofit Founder', 2000, 'Finance Lifelong Learner', 2000]
    },
    vi: {
      bio: 'Sinh viên Tài chính tại Đại học Northeastern với kinh nghiệm tại Ngân hàng Thương mại Phương Đông, HFIC, Westlake Securities, Springer Capital, Cavalina Property Group và VinaCapital. Người sáng lập Quỹ AidWorks.',
      peopleImpacted: '93.500+ Người Được Hỗ Trợ',
      volunteers: '100+ Tình nguyện viên Lãnh đạo',
      scrollDown: 'Cuộn xuống',
      resume: 'Sơ yếu lý lịch',
      contact: 'Liên hệ',
      connectOnLinkedin: 'Kết nối trên LinkedIn',
      typewriterTexts: ['Sinh viên Tài chính', 2000, 'Doanh nhân', 2000, 'Người sáng lập Phi lợi nhuận', 2000, 'Học viên Tài chính Suốt đời', 2000]
    }
  };
  const t = translations[language];
  return <section id="hero" className="boston-skyline relative w-full min-h-screen flex items-center justify-center overflow-hidden" data-id="element-261">
      {/* Particles container */}
      <div ref={particlesRef} className="particles" data-id="element-262"></div>
      {/* Overlay with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" data-id="element-263"></div>
      {/* Content container */}
      <div className="absolute inset-0 flex items-center justify-center z-10" data-id="element-264">
        <div className="container mx-auto px-4 flex flex-col items-center justify-center" style={{
        transform: isMobile ? 'none' : `translate(${mousePosition.x * 15}px, ${mousePosition.y * 15}px)`
      }} data-id="element-265">
          {/* Language toggle - top right */}
          <div className="absolute top-6 right-6 z-20" data-id="element-266"></div>
          {/* Main content - centered */}
          <div className="w-full max-w-4xl mx-auto text-center" data-id="element-267">
            {/* Name with animated reveal */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display font-bold tracking-tight reveal-text mb-4 md:mb-6" data-id="element-268">
              <span className="gradient-text glow-text" data-id="element-269">Trung Nguyen</span>
            </h1>
            {/* Animated typewriter */}
            <div className="h-8 sm:h-10 mb-4 md:mb-6" data-id="element-270">
              <TypeAnimation sequence={t.typewriterTexts} wrapper="span" speed={50} className="text-xl sm:text-2xl md:text-3xl text-accent-cyan" repeat={Infinity} data-id="element-271" />
            </div>
            {/* Bio description */}
            <p className="text-muted text-base sm:text-lg max-w-2xl mx-auto mb-8 opacity-0 animate-[fadeIn_1s_forwards]" style={{
            animationDelay: '0.8s'
          }} data-id="element-272">
              {t.bio}
            </p>
            {/* Highlight badges */}
            <div className="flex flex-wrap justify-center gap-3 mb-8" data-id="element-273">
              <div className="bg-secondary/70 backdrop-blur-sm px-4 py-2 rounded-full text-sm sm:text-base opacity-0 animate-[fadeIn_1s_forwards]" style={{
              animationDelay: '1s'
            }} data-id="element-274">
                <span className="text-accent-cyan font-semibold" data-id="element-275">
                  {t.peopleImpacted.split(' ')[0]}
                </span>{' '}
                {t.peopleImpacted.split(' ').slice(1).join(' ')}
              </div>
              <div className="bg-secondary/70 backdrop-blur-sm px-4 py-2 rounded-full text-sm sm:text-base opacity-0 animate-[fadeIn_1s_forwards]" style={{
              animationDelay: '1.2s'
            }} data-id="element-276">
                <span className="text-accent-violet font-semibold" data-id="element-277">
                  {t.volunteers.split(' ')[0]}
                </span>{' '}
                {t.volunteers.split(' ').slice(1).join(' ')}
              </div>
            </div>
            {/* Primary buttons */}
            <div className="flex flex-wrap justify-center gap-4 mb-4 opacity-0 animate-[fadeIn_1s_forwards]" style={{
            animationDelay: '1.6s'
          }} data-id="element-278">
              <a href="https://www.linkedin.com/in/realtrungnguyen" target="_blank" rel="noopener noreferrer" className="gradient-button w-full sm:w-auto px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 ripple-button" onClick={createRipple} data-id="element-279">
                <LinkedinIcon size={20} data-id="element-280" />
                LinkedIn
              </a>
              <a href="https://drive.google.com/file/d/1uaQqtMCqQRsS2yBadyDQ0cmCFu7Edq3t/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="gradient-button w-full sm:w-auto px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 ripple-button" onClick={createRipple} style={{
              background: 'linear-gradient(90deg, #F59E0B, #8B5CF6)'
            }} data-id="element-281">
                <FileTextIcon size={20} data-id="element-282" />
                {t.resume}
              </a>
            </div>
            {/* Secondary buttons */}
            <div className="flex flex-wrap justify-center gap-4 opacity-0 animate-[fadeIn_1s_forwards]" style={{
            animationDelay: '1.8s'
          }} data-id="element-283">
              <a href="https://airevoapp.com" target="_blank" rel="noopener noreferrer" className="gradient-button w-full sm:w-auto px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 ripple-button" onClick={createRipple} style={{
              background: 'linear-gradient(90deg, #22D3EE, #4F46E5)'
            }} data-id="element-284">
                <ExternalLinkIcon size={20} data-id="element-285" />
                Airevo
              </a>
              <a href="https://aidworksfoundation.org" target="_blank" rel="noopener noreferrer" className="gradient-button w-full sm:w-auto px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 ripple-button" onClick={createRipple} style={{
              background: 'linear-gradient(90deg, #22D3EE, #0891B2)'
            }} data-id="element-286">
                <ExternalLinkIcon size={20} data-id="element-287" />
                AidWorks
              </a>
              <a href="mailto:officialtrungnguyen@gmail.com" className="bg-secondary hover:bg-secondary/80 text-primary w-full sm:w-auto px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 transition-all border border-accent-cyan/30 ripple-button" onClick={createRipple} data-id="element-288">
                <MailIcon size={20} data-id="element-289" />
                {t.contact}
              </a>
            </div>
          </div>
          {/* Scroll down indicator */}
          <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 opacity-0 animate-[fadeIn_1s_forwards]" style={{
          animationDelay: '2s'
        }} data-id="element-290">
            <button onClick={scrollToNext} className="flex flex-col items-center text-primary/70 hover:text-accent-cyan transition-colors animate-bounce-slow" data-id="element-291">
              <span className="text-sm mb-2" data-id="element-292">{t.scrollDown}</span>
              <ArrowDownIcon size={20} data-id="element-293" />
            </button>
          </div>
        </div>
      </div>
    </section>;
};
export default Hero;